import { useEffect, useMemo, useRef } from 'react';
import { Canvas, events as createPointerEvents, useFrame, useThree } from '@react-three/fiber';
import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  MathUtils,
  MeshBasicMaterial,
} from 'three';
import {
  fitGridContentToViewport,
  normalizeGridViewState,
} from '../utils/gridViewState';
import {
  buildGridCellTrianglePositions,
  buildGridLinePositions,
  getGridWorldDimensions,
} from '../utils/gridWorldGeometry';

const createGeometry = (positions) => {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.computeBoundingSphere();
  return geometry;
};

const createNonInteractiveEvents = (store) => ({
  ...createPointerEvents(store),
  enabled: false,
  connect: () => {},
  disconnect: () => {},
});

function CameraFrame({ viewState, aspectRatio, fitMode, onCameraFrame }) {
  const targetRef = useRef(normalizeGridViewState(viewState));
  const currentRef = useRef(normalizeGridViewState(viewState));
  const onCameraFrameRef = useRef(onCameraFrame);
  const { invalidate } = useThree();
  onCameraFrameRef.current = onCameraFrame;

  useEffect(() => {
    targetRef.current = normalizeGridViewState(viewState);
    invalidate();
  }, [invalidate, viewState?.centerX, viewState?.centerY, viewState?.zoom]);

  useFrame((state, delta) => {
    const { width, height } = state.size;
    if (!width || !height) return;

    const viewport = { width, height, aspectRatio, fitMode };
    const content = fitGridContentToViewport(width, height, aspectRatio, fitMode);
    const camera = state.camera;
    const target = normalizeGridViewState(targetRef.current, viewport);
    const currentView = normalizeGridViewState(currentRef.current, viewport);
    const current = normalizeGridViewState({
      zoom: MathUtils.damp(currentView.zoom, target.zoom, 12, delta),
      centerX: MathUtils.damp(currentView.centerX, target.centerX, 12, delta),
      centerY: MathUtils.damp(currentView.centerY, target.centerY, 12, delta),
    }, viewport);
    currentRef.current = current;

    camera.left = -width / 2;
    camera.right = width / 2;
    camera.top = height / 2;
    camera.bottom = -height / 2;
    camera.position.set(
      (current.centerX - 0.5) * content.width,
      (0.5 - current.centerY) * content.height,
      100
    );
    camera.zoom = current.zoom;
    camera.updateProjectionMatrix();
    onCameraFrameRef.current?.(current);

    const unsettled =
      Math.abs(current.zoom - target.zoom) > 0.001 ||
      Math.abs(current.centerX - target.centerX) > 0.001 ||
      Math.abs(current.centerY - target.centerY) > 0.001;
    if (unsettled) invalidate();
  });

  return null;
}

function GridGeometry({ gridType, gridCols, gridRows, fitMode }) {
  const { size } = useThree();
  const world = useMemo(
    () => getGridWorldDimensions(gridCols, gridRows, gridType),
    [gridCols, gridRows, gridType]
  );
  const linePositions = useMemo(
    () => buildGridLinePositions(gridCols, gridRows, gridType),
    [gridCols, gridRows, gridType]
  );
  const lineGeometry = useMemo(() => createGeometry(linePositions), [linePositions]);
  const fillPositions = useMemo(() => {
    if (gridType !== 'hex') return [];
    const cells = Array.from({ length: gridCols * gridRows }, (_, index) => ({
      x: index % gridCols,
      y: Math.floor(index / gridCols),
    }));
    return buildGridCellTrianglePositions(cells, gridCols, gridRows, 'hex');
  }, [gridCols, gridRows, gridType]);
  const fillGeometry = useMemo(() => createGeometry(fillPositions), [fillPositions]);
  const content = fitGridContentToViewport(size.width, size.height, world.width / world.height, fitMode);

  useEffect(() => () => lineGeometry.dispose(), [lineGeometry]);
  useEffect(() => () => fillGeometry.dispose(), [fillGeometry]);

  return (
    <group scale={[content.width / world.width, content.height / world.height, 1]}>
      <mesh position={[0, 0, -0.5]}>
        <planeGeometry args={[world.width, world.height]} />
        <meshBasicMaterial color='#313132' />
      </mesh>
      {gridType === 'hex' && (
        <mesh geometry={fillGeometry} position={[0, 0, -0.2]}>
          <meshBasicMaterial color='#ffffff' transparent opacity={0.03} side={DoubleSide} />
        </mesh>
      )}
      <lineSegments geometry={lineGeometry}>
        <lineBasicMaterial
          color='#ffffff'
          transparent
          opacity={gridType === 'hex' ? 0.09 : 0.07}
        />
      </lineSegments>
    </group>
  );
}

function GridWorldScene({ gridType, gridCols, gridRows, viewState, fitMode, onCameraFrame }) {
  const world = getGridWorldDimensions(gridCols, gridRows, gridType);
  return (
    <>
      <CameraFrame
        viewState={viewState}
        fitMode={fitMode}
        aspectRatio={world.width / world.height}
        onCameraFrame={onCameraFrame}
      />
      <GridGeometry gridType={gridType} gridCols={gridCols} gridRows={gridRows} fitMode={fitMode} />
    </>
  );
}

export default function GridWorldCanvas({
  gridType,
  gridCols,
  gridRows,
  viewState,
  fitMode = 'contain',
  onCameraFrame,
  className = '',
}) {
  return (
    <Canvas
      className={`grid_world_canvas${className ? ` ${className}` : ''}`}
      orthographic
      camera={{ position: [0, 0, 100], zoom: 1 }}
      events={createNonInteractiveEvents}
      dpr={[1, 2]}
      frameloop='demand'
      gl={{ alpha: true, antialias: true, powerPreference: 'high-performance' }}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      fallback={<div className='grid_world_canvas_fallback' aria-hidden='true' />}
    >
      <GridWorldScene
        gridType={gridType}
        gridCols={gridCols}
        gridRows={gridRows}
        viewState={viewState}
        fitMode={fitMode}
        onCameraFrame={onCameraFrame}
      />
    </Canvas>
  );
}